import { Component, Input, OnInit, OnDestroy, Renderer2 } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonSettings, INCPCCPayment } from 'epay-library';
import { EpayExtendedService } from 'src/app/helpers/services/epay-extended.service';
import { SMIPayCCSessionToken } from '../../../models/SMI Pay- Credit Card/smi-pay-cc-session-token.model';
import { SMIPayRefreshToken } from '../../../models/SMI Pay- Credit Card/smi-pay-refresh-token.model';
import { SMIPayCommonSettings } from 'src/app/appload/service/appload.service';

export interface SmiPayIFrameConfig {
  hostedFormUrl: string;
  transactionId: string;
  merchantId: string;
  sessionToken: string;
  redirectDelaySeconds: number;
  description: string;
  authToken: string;
  parentDomain: string;
  //restartCallbackUrl: string;
  successCallbackUrl: string;
  canceledCallbackUrl: string;
}

@Component({
  selector: 'smi-pay-iframe',
  templateUrl: './smi-pay-iframe.component.html',
})
export class SmiPayIFrameComponent implements OnInit, OnDestroy {
  pagetitle='SMI Pay';
  //private readonly hostedFormOrigin = 'https://hpp.uat.smipay.com';

  @Input() creditCardPayment: INCPCCPayment = {} as INCPCCPayment;
  @Input() transactionId: string = '';

  iframeUrl: string = '';
  urlSafe!: SafeResourceUrl;
  loading = true;

  sessionToken: string = '';
  accessToken: string = '';

  amount: number = 0;

  merchantId: string = '';
  hostedFormUrl: string = '';
  redirectDelaySeconds: string = '';
  description: string = '';
  parentDomain: string = '';
  successCallbackUrl: string = '';
  cancelledCallbackUrl: string = '';

  errorMessage: string = '';

  config: SmiPayIFrameConfig = {} as SmiPayIFrameConfig;

  sessionTokenRequest: SMIPayCCSessionToken = {} as SMIPayCCSessionToken;
  refreshTokenRequest: SMIPayRefreshToken = {} as SMIPayRefreshToken;
  private readonly minLoadingMs = 800;
  private loadingStartMs = 0;
  private loadingTimer: ReturnType<typeof setTimeout> | null = null;
  private hasReceivedMessage = false;
  private iframeMessageWatchdog: ReturnType<typeof setTimeout> | null = null;

  baseurl = '';

  hostedFormOrigin: string = '';

  constructor(
    private router: Router, 
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private extendedService: EpayExtendedService,
    private renderer: Renderer2
  ) {
      this.baseurl = CommonSettings.BaseApi;

      this.hostedFormOrigin = SMIPayCommonSettings.HostedFormUrl;

      this.merchantId = SMIPayCommonSettings.MerchantId;
      this.hostedFormUrl = SMIPayCommonSettings.HostedFormUrl;
      this.redirectDelaySeconds = SMIPayCommonSettings.RedirectDelaySeconds;
      this.description = SMIPayCommonSettings.Description;
      this.successCallbackUrl = SMIPayCommonSettings.SuccessCallbackUrl;
      this.cancelledCallbackUrl = SMIPayCommonSettings.CancelledCallbackUrl;

      if (window.location.origin === 'http://localhost:4200') {
        this.parentDomain = 'https://wv-epay.dev.smartchildsupport.com/';
      }
      else {
        this.parentDomain = window.location.origin;
      }
  }

  private setImmersiveMode(enabled: boolean): void {
    if (enabled) {
      this.renderer.addClass(document.body, 'smi-immersive-mode');
    } else {
      this.renderer.removeClass(document.body, 'smi-immersive-mode');
    }
  }

  ngOnInit(): void {
    window.addEventListener('message', this.handleMessage);
    //console.log('SMI postMessage listener registered');
    this.runMessageListenerSelfTest();
    
    this.getSessionToken();
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.handleMessage);
    this.setImmersiveMode(false);
    if (this.loadingTimer) {
      clearTimeout(this.loadingTimer);
      this.loadingTimer = null;
    }
    if (this.iframeMessageWatchdog) {
      clearTimeout(this.iframeMessageWatchdog);
      this.iframeMessageWatchdog = null;
    }
  }

  private runMessageListenerSelfTest(): void {
    setTimeout(() => {
      window.postMessage({ type: 'SMI_LISTENER_SELF_TEST' }, window.location.origin);
    }, 0);


  }

  private startIframeMessageWatchdog(): void {
    if (this.iframeMessageWatchdog) {
      clearTimeout(this.iframeMessageWatchdog);
    }

    this.hasReceivedMessage = false;
    this.iframeMessageWatchdog = setTimeout(() => {
      if (!this.hasReceivedMessage) {
        console.warn('No iframe postMessage events received within 5 minutes of iframe load.');
      }
    }, 300000);
  }

  private startLoading(): void {
    if (this.loadingTimer) {
      clearTimeout(this.loadingTimer);
      this.loadingTimer = null;
    }

    this.loadingStartMs = Date.now();
    this.loading = true;
  }

  private stopLoadingWithMinimumDelay(): void {
    const elapsedMs = Date.now() - this.loadingStartMs;
    const remainingMs = this.minLoadingMs - elapsedMs;

    if (remainingMs <= 0) {
      this.loading = false;
      return;
    }

    this.loadingTimer = setTimeout(() => {
      this.loading = false;
      this.loadingTimer = null;
    }, remainingMs);
  }

  getSessionToken(){
    this.sessionTokenRequest = {
      merchantId: this.merchantId,
      //successCallbackUrl: this.successCallbackUrl,
      //cancelledCallbackUrl: this.cancelledCallbackUrl,
      currency: 'USD',
      creditCard: {
        address1: this.creditCardPayment.addressLine1,
        address2: this.creditCardPayment.addressLine2,
        amount: this.creditCardPayment.amount, // Use the amount entered by the user, not the total amount with fees. SMI Pay will handle the fee calculation and addition to the total amount.
        city: this.creditCardPayment.addressCity,
        confirmationNumber: '',
        country: this.creditCardPayment.addressCountry,
        email: this.creditCardPayment.email,
        fullName: this.creditCardPayment.firstName + ' ' + this.creditCardPayment.lastName,
        last4OfCard: '',
        payerType: this.creditCardPayment.payerType,
        paymentType: '',
        paymentSource: '',
        phone: this.creditCardPayment.phone,
        primaryId: this.creditCardPayment.primaryId,
        quaternaryId: this.creditCardPayment.quaternaryId,
        recipientId: '',
        recipientSsn: '',
        secondaryFullName: '',
        secondaryId: this.creditCardPayment.secondaryId,
        ssn: this.creditCardPayment.ssn,
        state: this.creditCardPayment.addressState,
        tertiaryId: this.creditCardPayment.tertiaryId,
        thirdPartyId: '',
        thirdPartyDataSource: '',
        zip4: '',
        zip5: this.creditCardPayment.addressZIP5
      }
    };  
    
    this.startLoading();
    this.errorMessage = '';

    this.extendedService.getSMIPaySessionToken(this.baseurl, this.sessionTokenRequest).subscribe({
      next: (response: any) => { 
        //console.log('Session Token Response', response);
        //console.log("Transaction ID:", this.transactionId);

        if (response.isOk) {
          this.sessionToken = response.sessionToken;
          this.accessToken = response.accessToken;
          //this.merchantId = response.merchantId;
          //this.transactionId = response.transactionId;
          this.amount = response.amount;
          //console.log('Session token retrieved successfully', response);

          //console.log("INITIAL TOKEN:", this.sessionToken);
          //  console.log("INITIAL ACCESS TOKEN:", this.accessToken);
        }
        else {
          this.errorMessage = response.errorMessage || 'Failed to retrieve session token. Please try again later.';
        }
        //console.log('Session Token Response', response);

        if (this.sessionToken === '' || this.transactionId === '') {
          this.errorMessage = 'Missing session token or transaction ID.';
          this.stopLoadingWithMinimumDelay();
          return;
        }
        else {
          this.loadIframe();
        }
      },      

      error: (error: any) => {  
        console.error('Error fetching session token:', error);  
        this.errorMessage = 'Failed to retrieve session token. Please try again later.';
        this.stopLoadingWithMinimumDelay();
      }
    });
  }

  refreshToken() {
    //console.log("Refreshing session...", this.sessionToken);
    //  console.log("Refreshing access...", this.accessToken);

    this.refreshTokenRequest = {
      accessToken: this.accessToken,
      amount: this.amount,
      currency: 'USD'
    };

    this.extendedService.refreshSMIPaySessionToken(this.baseurl, this.refreshTokenRequest).subscribe({
      next: (response: any) => { 
        if (response) {  
          this.sessionToken = response;
          //console.log("REFRESHED TOKEN:", this.sessionToken);
          //console.log("SESSION TOKEN:", response);

          // Some hosted forms request a refreshed token via postMessage.
          // Send it back to the iframe directly so a full iframe reload is not required.
          this.sendMessageToIframe({
            type: 'SMI_SESSION_TOKEN_REFRESHED',
            token: this.sessionToken,
            bearerToken: this.accessToken,
          }, this.hostedFormOrigin);

          //console.log('Session token refreshed successfully');
        }
        else {
          this.errorMessage = response.errorMessage || 'Failed to refresh session token. Please try again later.';
          //console.error('Failed to refresh session token:', response.errorMessage);
        }
      },
      error: (error: any) => {  
        this.errorMessage = 'Failed to refresh session token. Please try again later.';
        //console.error('Error refreshing session token:', error);  
      }
    });
  }

  private sendMessageToIframe(message: any, targetOrigin: string): void {
    const iframeElement = document.getElementById('hosted-form') as HTMLIFrameElement | null;

    if (!iframeElement || !iframeElement.contentWindow) {
      //console.warn('hosted-form iframe is not ready for postMessage.');
      return;
    }

    let effectiveTargetOrigin = targetOrigin;

    try {
      const iframeOrigin = new URL(iframeElement.src, window.location.origin).origin;
      if (iframeOrigin !== targetOrigin) {
        console.warn('postMessage target origin mismatch. Using iframe current origin instead.', {
          requestedTargetOrigin: targetOrigin,
          iframeOrigin,
        });
        effectiveTargetOrigin = iframeOrigin;
      }
    } catch {
      // Keep the provided target origin if iframe src cannot be parsed.
    }

    iframeElement.contentWindow.postMessage(message, effectiveTargetOrigin);
  }

  loadIframe() {
    this.startLoading();

    if (this.sessionToken === '' || this.transactionId === '') {
      this.errorMessage = 'Missing session token or transaction ID.';
      this.stopLoadingWithMinimumDelay();
      return;
    }
    if (!this.hostedFormUrl || !this.merchantId || !this.redirectDelaySeconds || !this.description || !this.successCallbackUrl || !this.cancelledCallbackUrl) {
      this.errorMessage = 'Missing hosted form URL or required parameters.';
      this.stopLoadingWithMinimumDelay();
      return;
    }

    // hook some of these values up to DB Common Settings values
    this.config = {
      hostedFormUrl: this.hostedFormUrl,
      transactionId: this.transactionId,
      merchantId: this.merchantId,
      sessionToken: this.sessionToken,
      redirectDelaySeconds: +this.redirectDelaySeconds,
      description: this.description,
      authToken: this.accessToken,
      parentDomain: this.parentDomain,
      //restartCallbackUrl: 'https://wv-epay.dev.smartchildsupport.com/home',
      successCallbackUrl: this.successCallbackUrl,
      canceledCallbackUrl: this.cancelledCallbackUrl
    };

    //console.log("TID ", this.transactionId)

    this.iframeUrl = this.getHostedFormUrl(this.config);
    this.urlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(this.iframeUrl);
    //console.log('Iframe URL:', this.iframeUrl);
  }

  getHostedFormUrl(params: any): string {
    const hostedUrl = new URL(params.hostedFormUrl);
    hostedUrl.searchParams.set('transactionId', params.transactionId + 'WV');
    hostedUrl.searchParams.set('merchantId', '8');
    hostedUrl.searchParams.set('merchantClientId', '');
    hostedUrl.searchParams.set('token', params.sessionToken);
    hostedUrl.searchParams.set('redirectDelaySeconds', params.redirectDelaySeconds);
    hostedUrl.searchParams.set('description', params.description);
    hostedUrl.searchParams.set('bearerToken', params.authToken);
    hostedUrl.searchParams.set('parentDomain', this.parentDomain);
    //hostedUrl.searchParams.set('SuccessCallbackURL', params.successCallbackUrl);
    //hostedUrl.searchParams.set('CanceledCallbackURL', params.canceledCallbackUrl);

    //console.log("HOSTED URL ", hostedUrl)
    return hostedUrl.toString();
  }

  private getNormalizedMessageType(msg: any): string {
    const rawType =
      msg?.type ??
      msg?.eventType ??
      msg?.messageType ??
      msg?.event ??
      msg?.data?.type ??
      msg?.data?.eventType ??
      msg?.payload?.type ??
      '';

    return String(rawType).trim().toUpperCase();
  }

  private routeToHome(): void {
    this.setImmersiveMode(false);
    this.router.navigate(['/home']);
  }

  handleMessage = (event: MessageEvent) => {
    this.hasReceivedMessage = true;
    //console.log('SMI raw message event:', event);

    let msg: any = event.data || {};
    if (typeof msg === 'string') {
      try {
        msg = JSON.parse(msg);
      } catch {
        msg = { type: msg };
      }
    }

    const normalizedType = this.getNormalizedMessageType(msg);
    if (!normalizedType) {
      console.warn('SMI message ignored (no type):', msg);
      return;
    }

    //console.log('SMI normalized message type:', normalizedType);

    switch (normalizedType) {
      case 'SMI_LISTENER_SELF_TEST':
        //console.log('SMI message listener self-test succeeded.');
        break;
      case 'SMI_PAYMENT_RESULT':
        this.handlePaymentResult(msg);
        break;
      case 'SMI_SESSION_EXPIRED_REDIRECT':
      case 'SMI_SESSION_EXPIRED':
      case 'SESSION_EXPIRED':
      case 'SMI_TIMEOUT':
      case 'TIMEOUT':
        this.routeToHome();
        break;

      case 'SMI_SESSION_REFRESH_REQUEST':
          this.refreshToken();
          break;

      case 'SMI_UNAUTHORIZED_RETURN':
      case 'SMI_UNAUTHORIZED':
          this.routeToHome();
          break; 
      default:
        //console.log('Unhandled SMI message type:', normalizedType, msg);
        break;
    }
  };

  async handlePaymentResult(msg: any) {
    const { result, data = {} } = msg;
    const { transactionId, processorTransactionId, status, successCallbackURL, CancelledCallbackURL } = data;

    //console.log("MSG ", msg)

    if (result === 'success') {
      //console.log('Payment successful with status:', status, 'Transaction ID:', transactionId, 'Processor Transaction ID:', processorTransactionId);
      // Optionally, send notification to your backend here
      if (successCallbackURL) {
        window.location.href = this.buildRedirectUrl(successCallbackURL, { Status: status || 'Success', externalId: transactionId || '', id: processorTransactionId });
      } else {
        this.router.navigate(['/creditcardsuccess']);
      }
      return;
    }

    if (result === 'failure') {
      //console.log('Payment failed with status:', status);
      if (CancelledCallbackURL) {
        window.location.href = this.buildRedirectUrl(CancelledCallbackURL, { Status: 'Failed' });
      } else {
        this.router.navigate(['/payment-declined']);
      }
      return;
    }

    if (result === 'cancel') {
      //console.log('Payment was cancelled by the user.');
      // Optionally, send notification to your backend here
      if (CancelledCallbackURL) {
        window.location.href = this.buildRedirectUrl(CancelledCallbackURL, { Status: 'Canceled' });
      } else {
        this.router.navigate(['/creditcardcancel']);
      }
      return;
    }
  }

  buildRedirectUrl(baseUrl: string, params: any): string {
    const url = new URL(baseUrl);
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.set(key, String(value));
    });
    return url.toString();
  }


  onIframeLoad() {
    //console.log('Iframe loaded successfully');
    this.startIframeMessageWatchdog();
    this.setImmersiveMode(true);
    this.stopLoadingWithMinimumDelay();
  }

  onIframeError() {
    //console.error('Iframe failed to load:', this.iframeUrl);
    this.setImmersiveMode(false);
    this.errorMessage = `Failed to load payment form from: ${this.iframeUrl}. The URL may be blocked by security policies (X-Frame-Options, CORS, or CSP headers).`;
    this.stopLoadingWithMinimumDelay();
  }
}